/**
 * Owner: Masa Bayu
 * File: CommerceOrdersPanel.tsx
 * Date:Time: 2026-09-15 22:25
 * Description: T3 orders + E2 payment controls — entitlement boundary ≠ Library/Reader.
 *   §466 S2/S3 — kawalan pembayaran hanya dipaparkan dengan sesi hub yang hidup
 *   (stor ledger gagal-keras tanpa akaun); pelawat ditawari log masuk, bukan butang mati.
 */

import { useState } from 'react';
import { isOrderDraftPaymentSafe } from '@qx/qxread-core';
import {
  applyDemoPaymentOutcome,
  beginDemoPayment,
  getDemoConfirmedOrder,
  getDemoEntitlementBatch,
  getDemoOrder,
  getDemoPaymentAttempt,
  replayDemoSucceededCallback,
  retryDemoPaymentAfterFailure,
} from './demo-cart-store';
import { getHubAuthAccountId, useHubAuthState } from '../publication/hub-auth';
import { accountEntryPath } from '../auth/account-gate';
import { formatMarketplacePriceOrUnknown } from '../home/marketplace/marketplace-locale';

export function CommerceOrdersPanel({
  onNavigate,
}: {
  readonly onNavigate: (path: string) => void;
}) {
  const [revision, setRevision] = useState(0);
  const authState = useHubAuthState();
  // §466 S2/S3 — pembukuan pembayaran ada pada akaun sesi hub yang nyata, jadi stor
  // gagal-keras tanpa sesi. Gerbang UI membaca sumber yang sama: pelawat tanpa sesi
  // hidup tidak ditawari butang yang pasti melempar ralat.
  const signedIn = authState === 'signed-in' && getHubAuthAccountId() !== null;
  const order = getDemoOrder();
  const confirmed = getDemoConfirmedOrder();
  const attempt = getDemoPaymentAttempt();
  const batch = getDemoEntitlementBatch();
  const revisionLabel = `rev-${String(revision)}`;

  // Setiap kawalan pembayaran mempunyai keadaan pra-syaratnya sendiri. Kira sekali
  // supaya bekas `role=group` tidak pernah muncul kosong — sebuah kumpulan tanpa
  // kawalan ialah hiasan, bukan affordance.
  const isPendingOrder = order !== null && order.state === 'pending' && confirmed === null;
  const canBeginPayment = isPendingOrder && (attempt === null || attempt.state === 'processing');
  const canSimulateOutcome =
    attempt !== null && attempt.state === 'processing' && confirmed === null;
  const canRetryPayment =
    attempt !== null && attempt.state === 'failed' && order?.state === 'pending';
  const canReplayCallback = attempt !== null && attempt.state === 'succeeded' && confirmed !== null;
  const hasPaymentControl =
    canBeginPayment || canSimulateOutcome || canRetryPayment || canReplayCallback;

  const bump = () => {
    setRevision((value) => value + 1);
  };

  return (
    <section
      className="route-panel"
      aria-labelledby="route-title"
      data-orders-revision={revisionLabel}
    >
      <p className="eyebrow">QXRead · T3-E2 Payment</p>
      <h2 id="route-title">Orders</h2>
      <p>
        Payment confirms the pending order and issues an entitlement boundary record. This surface
        does not open Library, launch Reader, or mutate QXP.
      </p>

      {order === null && confirmed === null ? (
        <p>No pending order draft yet. Prepare checkout first.</p>
      ) : null}

      {order !== null ? (
        <dl className="publication-facts" aria-label="Pending order facts">
          <div>
            <dt>Order</dt>
            <dd>
              <code>{order.orderId}</code>
            </dd>
          </div>
          <div>
            <dt>State</dt>
            <dd>{confirmed !== null ? 'superseded_by_confirmed' : order.state}</dd>
          </div>
          <div>
            <dt>Buyer total</dt>
            <dd>
              {formatMarketplacePriceOrUnknown(
                order.totals.buyerTotalMinorUnits,
                order.totals.currency,
                `Price unavailable (${order.totals.currency})`,
              )}
            </dd>
          </div>
          <div>
            <dt>Payment / Entitlement</dt>
            <dd>
              {String(order.paymentAttemptId)} / {String(order.entitlementId)} · safe=
              {String(isOrderDraftPaymentSafe(order))}
            </dd>
          </div>
        </dl>
      ) : null}

      {attempt !== null ? (
        <dl className="publication-facts" aria-label="Payment attempt status">
          <div>
            <dt>Payment attempt</dt>
            <dd>
              <code>{attempt.attemptId}</code>
            </dd>
          </div>
          <div>
            <dt>Payment state</dt>
            <dd aria-live="polite">{attempt.state}</dd>
          </div>
        </dl>
      ) : null}

      {confirmed !== null ? (
        <dl className="publication-facts" aria-label="Confirmed order facts">
          <div>
            <dt>Confirmed order</dt>
            <dd>
              <code>{confirmed.orderId}</code>
            </dd>
          </div>
          <div>
            <dt>Confirmed state</dt>
            <dd>{confirmed.state}</dd>
          </div>
          <div>
            <dt>Entitlement batch</dt>
            <dd>
              {confirmed.entitlementBatchId === null
                ? 'null'
                : `${confirmed.entitlementBatchId} · count=${String(batch?.entitlements.length ?? 0)} · opensLibrary=false`}
            </dd>
          </div>
        </dl>
      ) : null}

      {signedIn ? (
        hasPaymentControl ? (
          <div role="group" aria-label="Payment controls">
            {canBeginPayment ? (
              <p>
                <button
                  type="button"
                  onClick={() => {
                    beginDemoPayment();
                    bump();
                  }}
                >
                  Begin payment
                </button>
              </p>
            ) : null}

            {canSimulateOutcome ? (
              <p>
                <button
                  type="button"
                  onClick={() => {
                    applyDemoPaymentOutcome('succeeded');
                    bump();
                  }}
                >
                  Simulate payment success
                </button>{' '}
                <button
                  type="button"
                  onClick={() => {
                    applyDemoPaymentOutcome('failed');
                    bump();
                  }}
                >
                  Simulate payment failure
                </button>{' '}
                <button
                  type="button"
                  onClick={() => {
                    applyDemoPaymentOutcome('cancelled');
                    bump();
                  }}
                >
                  Simulate payment cancel
                </button>
              </p>
            ) : null}

            {canRetryPayment ? (
              <p>
                <button
                  type="button"
                  onClick={() => {
                    retryDemoPaymentAfterFailure();
                    bump();
                  }}
                >
                  Retry payment
                </button>
              </p>
            ) : null}

            {canReplayCallback ? (
              <p>
                <button
                  type="button"
                  onClick={() => {
                    replayDemoSucceededCallback();
                    bump();
                  }}
                >
                  Replay success callback
                </button>
              </p>
            ) : null}
          </div>
        ) : null
      ) : (
        <p role="status">
          <a
            href={accountEntryPath(false, '/orders')}
            onClick={(event) => {
              event.preventDefault();
              onNavigate(accountEntryPath(false, '/orders'));
            }}
          >
            Sign in
          </a>{' '}
          to open a payment ledger — orders and entitlements are booked to a real QXRead hub
          account.
        </p>
      )}

      <p className="route-panel__meta" role="status">
        T3-E2 · Payment → Order confirmed · Entitlement boundary · Library is T4 · Reader T5 closed
      </p>
    </section>
  );
}
